
import { useState, useCallback, useMemo } from 'react';
import { useMutation } from '@tanstack/react-query';
import { BusinessIdea, Blueprint, Language, AIService } from '../types';
import { supabaseService } from '../services/supabaseService';
import { affiliateService } from '../services/affiliateService';

export const useBlueprintEngine = (aiService: AIService, language: Language, userId?: string) => {
  const [selectedIdea, setSelectedIdea] = useState<BusinessIdea | null>(null);
  const [blueprint, setBlueprint] = useState<Blueprint | null>(null);
  const [currentBlueprintId, setCurrentBlueprintId] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: async (variables: { idea: BusinessIdea, niche: string }) => {
      // Check cache first
      if (variables.idea.cachedBlueprint) {
        return {
          blueprint: variables.idea.cachedBlueprint,
          publishedId: variables.idea.id,
          isCached: true
        };
      }

      // 1. Generate Raw Blueprint via AI (Pure Generation)
      let finalBlueprint = await aiService.generateSystemBlueprint(variables.idea, language);
      
      // 2. Enrich with Business Logic (Affiliates)
      finalBlueprint = await affiliateService.enrichBlueprint(finalBlueprint);

      let publishedId: string | null = null;
      
      // 3. Publish / Persist
      try {
        publishedId = await supabaseService.publishBlueprint(variables.niche, variables.idea, finalBlueprint, userId);
      } catch (publishErr) {
        console.warn("Auto-publish failed", publishErr);
      }

      return {
        blueprint: finalBlueprint,
        publishedId,
        isCached: false
      };
    },
    onSuccess: (data, variables) => {
      setBlueprint(data.blueprint);
      if (data.publishedId) setCurrentBlueprintId(data.publishedId);
      // If cached, we simulate a small delay in UI elsewhere, or just set immediately here.
    }
  });

  const createBlueprint = useCallback(async (idea: BusinessIdea, niche: string) => {
    setSelectedIdea(idea);
    setCurrentBlueprintId(null);
    const result = await mutation.mutateAsync({ idea, niche });
    
    return result.publishedId;
  }, [mutation]);

  const updateBlueprint = useCallback((updates: Partial<Blueprint>) => {
    setBlueprint(prev => prev ? { ...prev, ...updates } : null);
  }, []);

  const updateIdea = useCallback((updates: Partial<BusinessIdea>) => {
    setSelectedIdea(prev => prev ? { ...prev, ...updates } : null);
  }, []);

  const clearBlueprint = useCallback(() => {
    setSelectedIdea(null);
    setBlueprint(null);
    setCurrentBlueprintId(null);
    mutation.reset();
  }, [mutation]);

  return useMemo(() => ({
    selectedIdea,
    setSelectedIdea,
    blueprint,
    setBlueprint,
    currentBlueprintId,
    setCurrentBlueprintId,
    createBlueprint,
    updateBlueprint,
    updateIdea,
    clearBlueprint,
    isGeneratingBlueprint: mutation.isPending,
    error: mutation.error
  }), [
    selectedIdea,
    blueprint,
    currentBlueprintId,
    createBlueprint,
    updateBlueprint,
    updateIdea,
    clearBlueprint,
    mutation.isPending,
    mutation.error
  ]);
};
